import { axiosInstance } from '../../utils/axiosApp';
import {
  createId,
  DEFAULT_SUBJECT_DURATION,
  groupSubjectActivities,
  MAX_PLAN_ACTIVITY_TIMES,
} from './model';
import {
  API_GAME_TO_UI,
  GAME_OPTION_IDS,
  GAME_REQUIRED_RUNTIME_TOOLS,
  GAME_TO_API,
  isGameOption,
} from './options';
import type {
  GroupPlanAssignmentsResponse,
  PlanStudioApiActivity,
  PlanStudioApiPlan,
  PlanStudioApiSubject,
  PlanStudioAssignmentState,
  PlanStudioBackendActivityType,
  PlanStudioBackendRuntimeTool,
  PlanStudioCapabilities,
  PlanStudioDraft,
  PlanStudioGroupCapabilities,
  PlanStudioPlansResponse,
  PlanStudioPlanStatus,
  PlanStudioSavedPlan,
  ProfilePlanLibrary,
  ProfilePlanSourceSetting,
  StudioOptionId,
  StudioSelection,
  StudioSubject,
  Translate,
} from './types';
import { SESSION_LENGTH_DEFAULT_MINUTES } from '../../utils/constants';

export const TOOL_TO_API: Record<
  'generateImages' | 'generateVideo',
  PlanStudioBackendRuntimeTool
> = {
  generateImages: 'generate_image',
  generateVideo: 'generate_video',
};

export const API_TOOL_TO_UI: Record<
  PlanStudioBackendRuntimeTool,
  StudioOptionId
> = {
  generate_image: 'generateImages',
  generate_video: 'generateVideo',
};

const EMPTY_SELECTIONS: StudioSelection = {
  generateImages: 0,
  generateVideo: 0,
  gapFill: 0,
  pronunciation: 0,
  clueDetective: 0,
  quiz: 0,
  oddWordOut: 0,
  oddPictureOut: 0,
  captionMatch: 0,
  categorySort: 0,
  sequenceOrder: 0,
  jigsawPuzzle: 0,
  memoryMatch: 0,
  letterTracing: 0,
};

const parseMinutes = (value: unknown, fallback: number): number => {
  const parsed =
    typeof value === 'number'
      ? value
      : Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : fallback;
};

const validateActivityTimes = (value: number): number => {
  if (
    !Number.isInteger(value) ||
    value < 1 ||
    value > MAX_PLAN_ACTIVITY_TIMES
  ) {
    throw new Error(
      `Plan activity repetitions must be an integer between 1 and ${MAX_PLAN_ACTIVITY_TIMES}.`
    );
  }
  return value;
};

const selectedRuntimeTools = (
  selections: StudioSelection
): PlanStudioBackendRuntimeTool[] => {
  const tools: PlanStudioBackendRuntimeTool[] = [];
  if (selections.generateImages > 0) {
    tools.push(TOOL_TO_API.generateImages);
  }
  if (selections.generateVideo > 0) {
    tools.push(TOOL_TO_API.generateVideo);
  }
  return tools;
};

const selectedActivities = (
  selections: StudioSelection
): PlanStudioBackendActivityType[] =>
  GAME_OPTION_IDS.filter((optionId) => selections[optionId] > 0).map(
    (optionId) => GAME_TO_API[optionId]
  );

const withRequiredRuntimeTools = (
  runtimeTools: PlanStudioBackendRuntimeTool[],
  activities: PlanStudioBackendActivityType[]
): PlanStudioBackendRuntimeTool[] => {
  const tools = new Set(runtimeTools);
  GAME_OPTION_IDS.forEach((optionId) => {
    if (!activities.includes(GAME_TO_API[optionId])) {
      return;
    }
    GAME_REQUIRED_RUNTIME_TOOLS[optionId].forEach((tool) => tools.add(tool));
  });
  return Array.from(tools);
};

export const buildGeneratePlanPayload = ({
  prompt,
  sessionTime,
  selections,
}: {
  prompt: string;
  sessionTime: number;
  selections: StudioSelection;
}) => {
  const activities = selectedActivities(selections);
  return {
    prompt: prompt.trim(),
    session_time: sessionTime,
    runtime_tools: withRequiredRuntimeTools(
      selectedRuntimeTools(selections),
      activities
    ),
    selected_activities: activities,
  };
};

export const buildImproveDescriptionPayload = (
  description: string | null | undefined,
  sessionTime: number
) => ({
  description: (description ?? '').trim(),
  session_time: sessionTime,
});

const planTitleFromUi = (
  title: string | undefined,
  prompt: string,
  subjects: StudioSubject[],
  fallbackTitle: string
): string =>
  title?.trim() ||
  subjects[0]?.title.trim() ||
  prompt
    .replace(/\s+/g, ' ')
    .trim()
    .split(/[.!?\n]/)
    .find(Boolean)
    ?.trim() ||
  fallbackTitle;

const activityGoal = (
  subject: StudioSubject,
  optionId: StudioOptionId,
  labels?: Record<StudioOptionId, string>
): string => {
  const description = subject.activities.find(
    (activity) => activity.optionId === optionId
  )?.description;
  if (description?.trim()) {
    return description.trim();
  }

  const label = labels?.[optionId] || optionId;
  const context = subject.summary.trim() || subject.title.trim();
  return context ? `${label}: ${context}` : label;
};

const toApiActivities = (
  subject: StudioSubject,
  labels?: Record<StudioOptionId, string>
): PlanStudioApiActivity[] => {
  return groupSubjectActivities(subject.activities)
    .map((group): PlanStudioApiActivity | null => {
      if (!isGameOption(group.optionId)) {
        return null;
      }

      return {
        type: GAME_TO_API[group.optionId],
        goal: activityGoal(subject, group.optionId, labels),
        times: validateActivityTimes(group.activities.length),
      };
    })
    .filter((activity): activity is PlanStudioApiActivity => activity !== null);
};

const toApiSubject = (
  subject: StudioSubject,
  labels?: Record<StudioOptionId, string>
): PlanStudioApiSubject => ({
  title: subject.title.trim(),
  description: subject.summary.trim(),
  estimated_minutes: parseMinutes(
    subject.duration,
    Number.parseInt(DEFAULT_SUBJECT_DURATION, 10)
  ),
  activities: toApiActivities(subject, labels),
});

export const buildCreatePlanPayload = ({
  title,
  prompt,
  sessionTime,
  selections,
  subjects,
  labels,
  fallbackTitle,
}: {
  title?: string;
  prompt: string;
  sessionTime: number;
  selections: StudioSelection;
  subjects: StudioSubject[];
  labels?: Record<StudioOptionId, string>;
  fallbackTitle: string;
}): PlanStudioApiPlan => {
  const apiSubjects = subjects.map((subject) => toApiSubject(subject, labels));
  const activities = apiSubjects.flatMap((subject) =>
    subject.activities.map((activity) => activity.type)
  );

  return {
    title: planTitleFromUi(title, prompt, subjects, fallbackTitle),
    session_time: sessionTime,
    runtime_tools: withRequiredRuntimeTools(
      selectedRuntimeTools(selections),
      activities
    ),
    subjects: apiSubjects,
  };
};

export const buildDraftFromApiPlan = (
  plan: PlanStudioApiPlan,
  t: Translate
): PlanStudioDraft => {
  const selections = { ...EMPTY_SELECTIONS };

  plan.runtime_tools.forEach((tool) => {
    selections[API_TOOL_TO_UI[tool]] = 1;
  });

  const subjects = plan.subjects.map((subject, index) => {
    const counts = new Map<StudioOptionId, number>();
    const activities = subject.activities.flatMap((activity) => {
      const optionId = API_GAME_TO_UI[activity.type];
      const times = validateActivityTimes(activity.times);
      counts.set(
        optionId,
        validateActivityTimes((counts.get(optionId) ?? 0) + times)
      );
      selections[optionId] = 1;
      return Array.from({ length: times }, () => ({
        id: createId(`activity-${optionId}`),
        optionId,
        description: activity.goal?.trim() || undefined,
      }));
    });

    return {
      id: createId('subject'),
      title:
        subject.title.trim() ||
        t('planAhead.studio.subjectFallback', { index: index + 1 }),
      summary: subject.description?.trim() || '',
      duration: String(
        parseMinutes(
          subject.estimated_minutes,
          Number.parseInt(DEFAULT_SUBJECT_DURATION, 10)
        )
      ),
      activities,
    };
  });

  return {
    planTitle: plan.title.trim(),
    lessonBrief: subjects
      .map((subject) => subject.summary || subject.title)
      .filter(Boolean)
      .join('\n'),
    lessonDuration: String(
      parseMinutes(plan.session_time, SESSION_LENGTH_DEFAULT_MINUTES)
    ),
    selections,
    subjects,
    savedAt: new Date().toISOString(),
  };
};

export const generateProfilePlan = async (
  profileId: string,
  payload: ReturnType<typeof buildGeneratePlanPayload>
): Promise<PlanStudioApiPlan> => {
  const response = await axiosInstance().post<PlanStudioApiPlan>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/generate`,
    payload
  );
  return response.data;
};

export const getProfilePlanCapabilities = async (
  profileId: string
): Promise<PlanStudioCapabilities> => {
  const response = await axiosInstance().get<PlanStudioCapabilities>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/capabilities`
  );
  return response.data;
};

export const getGroupPlanCapabilities = async (
  groupId: string
): Promise<PlanStudioGroupCapabilities> => {
  const response = await axiosInstance().get<PlanStudioGroupCapabilities>(
    `/api/groups/${encodeURIComponent(groupId)}/plans/capabilities`
  );
  return response.data;
};

export const generateGroupPlan = async (
  groupId: string,
  payload: ReturnType<typeof buildGeneratePlanPayload>
): Promise<PlanStudioApiPlan> => {
  const response = await axiosInstance().post<PlanStudioApiPlan>(
    `/api/groups/${encodeURIComponent(groupId)}/plans/generate`,
    payload
  );
  return response.data;
};

export const improveProfilePlanDescription = async (
  profileId: string,
  payload: ReturnType<typeof buildImproveDescriptionPayload>
): Promise<{ description: string }> => {
  const response = await axiosInstance().post<{ description: string }>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/improve-description`,
    payload
  );
  return response.data;
};

export const improveGroupPlanDescription = async (
  groupId: string,
  payload: ReturnType<typeof buildImproveDescriptionPayload>
): Promise<{ description: string }> => {
  const response = await axiosInstance().post<{ description: string }>(
    `/api/groups/${encodeURIComponent(groupId)}/plans/improve-description`,
    payload
  );
  return response.data;
};

export const createProfilePlan = async (
  profileId: string,
  payload: PlanStudioApiPlan
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().post<PlanStudioSavedPlan>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans`,
    payload
  );
  return response.data;
};

export const createGroupPlan = async (
  groupId: string,
  payload: PlanStudioApiPlan
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().post<PlanStudioSavedPlan>(
    `/api/groups/${encodeURIComponent(groupId)}/plans`,
    payload
  );
  return response.data;
};

interface ListProfilePlansOptions {
  status?: PlanStudioPlanStatus;
  assignmentState?: PlanStudioAssignmentState;
}

export type ListProfilePlanSourcesOptions = ListProfilePlansOptions;

export const listProfilePlans = async (
  profileId: string,
  options: PlanStudioPlanStatus | ListProfilePlansOptions = 'active'
): Promise<PlanStudioSavedPlan[]> => {
  const params =
    typeof options === 'string'
      ? { status: options }
      : {
          status: options.status ?? 'active',
          assignment_state: options.assignmentState,
        };

  const response = await axiosInstance().get<PlanStudioPlansResponse>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans`,
    { params }
  );
  return response.data.plans;
};

export const listProfilePlanSources = async (
  profileId: string,
  options: PlanStudioPlanStatus | ListProfilePlanSourcesOptions = 'active'
): Promise<ProfilePlanLibrary> => {
  const params =
    typeof options === 'string'
      ? { status: options }
      : {
          status: options.status ?? 'active',
          assignment_state: options.assignmentState,
        };

  const response = await axiosInstance().get<ProfilePlanLibrary>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/sources`,
    { params }
  );
  return response.data;
};

export const updateProfilePlanSourceEnabled = async (
  profileId: string,
  groupId: string,
  enabled: boolean
): Promise<ProfilePlanSourceSetting> => {
  const response = await axiosInstance().patch<ProfilePlanSourceSetting>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/sources/group/${encodeURIComponent(groupId)}`,
    { enabled_for_profile: enabled }
  );
  return response.data;
};

export const listGroupPlans = async (
  groupId: string,
  status: PlanStudioPlanStatus = 'active'
): Promise<PlanStudioSavedPlan[]> => {
  const response = await axiosInstance().get<PlanStudioPlansResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/plans`,
    { params: { status } }
  );
  return response.data.plans;
};

export const listGroupPlanAssignments = async (
  groupId: string,
  planId: string,
  state: PlanStudioAssignmentState
): Promise<GroupPlanAssignmentsResponse> => {
  const response = await axiosInstance().get<GroupPlanAssignmentsResponse>(
    `/api/groups/${encodeURIComponent(groupId)}/plans/${encodeURIComponent(planId)}/assignments`,
    { params: { state } }
  );
  return response.data;
};

export const getProfilePlan = async (
  profileId: string,
  planId: string
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().get<PlanStudioSavedPlan>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/${encodeURIComponent(planId)}`
  );
  return response.data;
};

export const getGroupPlan = async (
  groupId: string,
  planId: string
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().get<PlanStudioSavedPlan>(
    `/api/groups/${encodeURIComponent(groupId)}/plans/${encodeURIComponent(planId)}`
  );
  return response.data;
};

export const updateProfilePlan = async (
  profileId: string,
  planId: string,
  payload: PlanStudioApiPlan
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().put<PlanStudioSavedPlan>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/${encodeURIComponent(planId)}`,
    payload
  );
  return response.data;
};

export const updateGroupPlan = async (
  groupId: string,
  planId: string,
  payload: PlanStudioApiPlan
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().put<PlanStudioSavedPlan>(
    `/api/groups/${encodeURIComponent(groupId)}/plans/${encodeURIComponent(planId)}`,
    payload
  );
  return response.data;
};

export const archiveProfilePlan = async (
  profileId: string,
  planId: string
): Promise<void> => {
  await axiosInstance().delete(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/${encodeURIComponent(planId)}`
  );
};

export const archiveGroupPlan = async (
  groupId: string,
  planId: string
): Promise<void> => {
  await axiosInstance().delete(
    `/api/groups/${encodeURIComponent(groupId)}/plans/${encodeURIComponent(planId)}`
  );
};

export const updateProfilePlanStatus = async (
  profileId: string,
  planId: string,
  status: PlanStudioPlanStatus
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().patch<PlanStudioSavedPlan>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/${encodeURIComponent(planId)}/status`,
    { status }
  );
  return response.data;
};

export const updateGroupPlanStatus = async (
  groupId: string,
  planId: string,
  status: PlanStudioPlanStatus
): Promise<PlanStudioSavedPlan> => {
  const response = await axiosInstance().patch<PlanStudioSavedPlan>(
    `/api/groups/${encodeURIComponent(groupId)}/plans/${encodeURIComponent(planId)}/status`,
    { status }
  );
  return response.data;
};

export interface PlanAutoGenerationSettings {
  enabled?: boolean;
}

export const updateProfilePlanAutoGenerationSettings = async (
  profileId: string,
  enabled: boolean
): Promise<boolean> => {
  const response = await axiosInstance().put<PlanAutoGenerationSettings>(
    `/api/profiles/${encodeURIComponent(profileId)}/plans/auto-generation/settings`,
    { enabled }
  );
  return Boolean(response.data?.enabled ?? enabled);
};
